import React, { createContext, useContext, useMemo, useState } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material';
import { CssBaseline } from '@mui/material';

const ColorModeContext = createContext({ toggleColorMode: () => {} });

export const useColorMode = () => {
  return useContext(ColorModeContext);
};

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    primary: {
      main: mode === 'light' ? '#3FC8DC' : '#3FC8DC',
      light: mode === 'light' ? '#E4B3FF' : '#E4B3FF',
      dark: mode === 'light' ? '#AE8686' : '#AE8686',
      contrastText: mode === 'light' ? '#FFFFFF' : '#FFFFFF',
    },
    secondary: {
      main: mode === 'light' ? '#FFB8E6' : '#FFB8E6',
      light: mode === 'light' ? '#FFD497' : '#FFD497',
      dark: mode === 'light' ? '#C65E4D' : '#C65E4D',
    },
    error: {
      main: '#9A5A5A',
      light: '#FFB5B5',
      dark: '#6E73D',
    },
    warning: {
      main: '#BU883C',
      light: '#FFE4B5',
      dark: '#6E73D',
    },
    info: {
      main: '#F1906F',
      light: '#B5F2FF',
      dark: '#6E73D',
    },
    success: {
      main: '#71006F',
      light: '#AEFFD1',
      dark: '#6E73D',
    },
    background: {
      default: mode === 'light' ? '#FFFFFF' : '#1A1B25',
      paper: mode === 'light' ? '#FFFFFF' : '#232430',
    },
    text: {
      primary: mode === 'light' ? '#2A2E3B' : '#E8E9F1',
      secondary: mode === 'light' ? '#636B7D' : '#A0A3B1',
    },
    grey: {
      50: '#F8F9FA',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    }
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
    h1: {
      fontFamily: "'Poppins', sans-serif",
      fontWeight: 700,
    },
    h2: {
      fontFamily: "'Poppins', sans-serif",
      fontWeight: 700,
    },
    h3: {
      fontFamily: "'Poppins', sans-serif",
      fontWeight: 600,
    },
    h4: {
      fontFamily: "'Poppins', sans-serif",
      fontWeight: 600,
    },
    h5: {
      fontFamily: "'Poppins', sans-serif",
      fontWeight: 500,
    },
    h6: {
      fontFamily: "'Poppins', sans-serif",
      fontWeight: 500,
    },
    subtitle1: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 500,
    },
    subtitle2: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 500,
    },
    body1: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 400,
    },
    body2: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 400,
    },
    button: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 500,
          padding: '8px 16px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
          },
        },
      },
    },
  },
});

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState('light');

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    []
  );

  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default ThemeProvider; 